import { AfterViewInit, ApplicationRef, Component, inject, Inject, NgZone, OnDestroy, OnInit, Renderer2, ViewChild } from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { OktaAuthStateService, OKTA_AUTH } from '@okta/okta-angular';
import { combineLatest, merge, take, timer } from 'rxjs';
import { filter } from 'rxjs/operators';
import { GoogleAnalyticsService } from './core/services/google-tag-manager.service';
import { Title } from '@angular/platform-browser';
import {
  ISelectedContract,
  LoggedInUserSettings,
} from './core/services/settings/logged-in-user-settings';
import { UserClaims } from './model/okta/userClaims';
import { UtilityService } from './core/services/utility.service';
import { PGHOktaService } from './core/services/pghokta.service';
import { datadogRum } from '@datadog/browser-rum';
import { PreInitSettingsService } from './core/services/settings/pre-init-setting.service';
import { ADPResourceService } from 'digital-core';
import { DOCUMENT } from '@angular/common';
import { PGHAppLoadService } from './core/services/pgh-app-load.service';
import { StorageService } from './core/services/storage.service';
import { OktaAuth } from '@okta/okta-auth-js';
import { AsappChatInitializer } from './core/services/asapp.initializer';
import { AsappCustomerInfo } from './model/asapp-chat/asapp-chat.dto';
import { GTMEvents } from './model/google-tag-manager/api-status';
import { OneTagService } from './core/services/one-tag.service';
import { MatSidenavContent } from '@angular/material/sidenav';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
})
export class AppComponent implements OnInit, AfterViewInit, OnDestroy {
  isAuthenticated: boolean;
  isSidenavShowing: boolean = false;
  contentLoaded = false;
  isNative: boolean;
  programName: string;

  private unListen: () => void;

  @ViewChild(MatSidenavContent) private content?: MatSidenavContent;

  constructor(
    @Inject(OKTA_AUTH) public oktaAuth: OktaAuth,
    public oktaAuthService: OktaAuthStateService,
    public router: Router,
    public route: ActivatedRoute,
    public title: Title,
    private loggedInUserSettings: LoggedInUserSettings,
    private utilityService: UtilityService,
    private preInitSettingsService: PreInitSettingsService,
    private pghOktaService: PGHOktaService,
    public resourceService: ADPResourceService,
    private appLoadService: PGHAppLoadService,
    private storageService: StorageService,
    @Inject(DOCUMENT) private document,
    private renderer: Renderer2,
    private googleAnalyticsService: GoogleAnalyticsService,
    private asappChatInitializer: AsappChatInitializer,
    private oneTagService: OneTagService,
    private appRef: ApplicationRef,
    private zone: NgZone
  ) {
    if (window.location.pathname.indexOf('accept-invite') > -1) {
      this.storageService.clearLocal();
    }
    this.route.queryParams.subscribe((params) => {
      this.isNative =
        (params.isNative && params.isNative.toLowerCase().trim() === 'true') ||
        this.utilityService.isNativeApp;
    });
  }

  async ngOnInit() {
    if (
      this.preInitSettingsService
        .getSettings()
        .isDatadogRUMEnabled?.toLowerCase() == 'true'
    ) {
      this.initDatadogRUM();
    }

    // Remove all Okta Storage when the User leaves the app only on mobile
    if (this.isNative) {
      window.onbeforeunload = () => {
        for (let key in localStorage) {
          if (key.startsWith('okta')) {
            localStorage.removeItem(key);
          }
        }
      };
    }

    this.setTheme();
    this.oktaAuthService.authState$.subscribe(
      (authState) => (this.isAuthenticated = authState.isAuthenticated)
    );
    this.isAuthenticated = this.pghOktaService.isTokenValid();

    combineLatest([
      this.loggedInUserSettings.$selectedContract,
      this.loggedInUserSettings.$userClaims,
      this.router.events.pipe(
        filter((event) => event instanceof NavigationEnd)
      ),
    ]).subscribe(([selectedContract, user, event]: [ISelectedContract, UserClaims, any]) => {
      // add one script with the correct key
      this.oneTagService.initializeOneTag();

      this.programName =
        this.resourceService.getPortalSetting('Global_ProgramName').Value;
      const titleArray = this.getTitle(
        this.router.routerState,
        this.router.routerState.root
      );
      if (titleArray && titleArray[0] === 'My Devices') {
        const titleValue = this.resourceService.getPortalSetting(
          'Global_MyDevices_Title'
        ).Value;
        if (titleValue) {
          titleArray[0] = titleValue;
        }
      }
      if (this.programName) {
        titleArray.push(this.programName);
      }
      const title = titleArray.join(' | ');
      this.title.setTitle(title);

      if (selectedContract !== null) {
        // Logged in
        this.googleAnalyticsService.setContractData(selectedContract);
        this.googleAnalyticsService.emitPageViewEvent();
        const customerInfo: AsappCustomerInfo = {
          isAuthenticated: true,
          encrypted_firstName: selectedContract.firstName,
          encrypted_lastName: selectedContract.lastName,
          encrypted_phoneNumber: selectedContract.oktaPrimaryPhone,
          lang: user?.locale
        };
        this.asappChatInitializer.initialize(customerInfo);
      } else {
        // Not logged in
        if (!this.isAuthenticated) {
          this.googleAnalyticsService.emitPageViewEvent();
        }
      }
    });

  }

  ngAfterViewInit() {
    // Add a click listener to the document
    this.unListen = this.renderer.listen(
      'document',
      'click',
      (event: Event) => {
        const target = event.target as HTMLElement;

        if (
          target &&
          (target.classList.contains('helpButtonEnabled') ||
            target.classList.contains('uiButton') ||
            target.classList.contains('helpButtonLabel'))
        ) {
          // console.log(target);
          this.googleAnalyticsService.emitClickEvent(
            GTMEvents.ChatOpen,
            'Live Support',
            undefined,
            undefined,
            undefined
          );
        }
      }
    );

    // Scroll to top of page on route change
    this.router.events
      .pipe(filter(e => e instanceof NavigationEnd))
      .subscribe(() => {
        // Optional: skip forced scroll when navigating to an anchor (fragment)
        const tree = this.router.parseUrl(this.router.url);
        if (tree.fragment) {
          return; // let Router anchorScrolling handle it
        }

        const whenStable$ = merge(
          this.appRef.isStable.pipe(filter(Boolean)),
          timer(0) // fallback: next macrotask in case isStable doesn't emit
        ).pipe(take(1));

        whenStable$.subscribe(() => {
          this.zone.runOutsideAngular(() => {
            requestAnimationFrame(() => {
              this.scrollTop();
              // backup scroll in case late microtasks add content
              setTimeout(() => this.scrollTop(), 0);
            });
          });
        });
      });
  }

  ngOnDestroy() {
    if (this.unListen) {
      this.unListen();
    }
  }

  // recursively get title
  getTitle(state, parent) {
    const data = [];

    if (parent && parent.snapshot?.data && parent.snapshot.data?.title) {
      data.push(parent.snapshot.data.title);
    }

    if (state && parent) {
      data.push(...this.getTitle(state, state.firstChild(parent)));
    }

    return data;
  }

  setSidenavShow(isShowing: boolean) {
    this.isSidenavShowing = isShowing;
  }

  initDatadogRUM() {
    const settings = this.preInitSettingsService.getSettings();
    datadogRum.init({
      applicationId: settings.datadogRUMConfig.applicationId,
      clientToken: settings.datadogRUMConfig.clientToken,
      site: 'us3.datadoghq.com',
      service: 'pocketgeekhome-myaccount',
      env: settings.environmentName,
      version: settings.datadogRUMConfig.version,
      sessionSampleRate: parseInt(settings.datadogRUMConfig.sampleRate),
      sessionReplaySampleRate: parseInt(
        settings.datadogRUMConfig.replaySampleRate
      ),
      trackResources: true,
      trackLongTasks: true,
      trackUserInteractions: true,
      defaultPrivacyLevel: 'mask-user-input',
    });
    datadogRum.setGlobalContextProperty(
      'app_name',
      this.preInitSettingsService.getSiteSettings().name
    );
    datadogRum.setGlobalContextProperty('app_env', settings.environmentName);
    datadogRum.setGlobalContextProperty(
      'cost_center',
      parseInt(settings.datadogRUMConfig.cid)
    );
    datadogRum.setGlobalContextProperty(
      'subcost_center',
      parseInt(settings.datadogRUMConfig.scid)
    );
  }

  setTheme() {
    this.appLoadService.isSiteResourcesLoaded.subscribe((value) => {
      if (value) {
        if (
          this.resourceService.getPortalSetting('Global_Theme').Value !== ''
        ) {
          this.document.body.classList.add(
            this.resourceService.getPortalSetting('Global_Theme').Value
          );
        }
      }
    });
  }


  private scrollTop() {
    if (this.content) {
      this.content.scrollTo({ top: 0, left: 0, behavior: 'auto' });
    } else {
      window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
    }
  }
}
